import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { HiArrowRight, HiLightBulb, HiOfficeBuilding, HiUserGroup } from 'react-icons/hi';

const ServicesSectionV2 = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const services = [
    {
      icon: HiLightBulb,
      title: '1:1 Leadership Mentorship',
      description: 'Personalized support for leaders seeking clarity, confidence, and stronger decision-making.',
      outcomes: [
        'Clear direction',
        'Better leadership presence',
        'Improved decision-making',
      ],
      cta: 'Book a Call',
      gradient: 'from-[#5B8FB9] to-[#7CB98F]',
      bgGradient: 'from-[#5B8FB9]/10 to-[#7CB98F]/10',
      borderColor: 'border-[#5B8FB9]/20',
      dotColor: 'bg-[#5B8FB9]',
    },
    {
      icon: HiOfficeBuilding,
      title: 'Organizational Well-being Consulting',
      description: 'Strategic support for companies looking to improve culture, reduce burnout, and enhance performance.',
      outcomes: [
        'Healthier teams',
        'Better engagement',
        'Sustainable growth',
      ],
      cta: 'Learn More',
      gradient: 'from-[#7CB98F] to-[#D4A5B5]',
      bgGradient: 'from-[#7CB98F]/10 to-[#D4A5B5]/10',
      borderColor: 'border-[#7CB98F]/20',
      dotColor: 'bg-[#7CB98F]',
    },
    {
      icon: HiUserGroup,
      title: 'Workshops & Trainings',
      description: 'Interactive sessions designed to develop leadership skills and strengthen team dynamics.',
      outcomes: [
        'Stronger collaboration',
        'Increased awareness',
        'Practical tools',
      ],
      cta: 'Explore Workshops',
      gradient: 'from-[#D4A5B5] to-[#5B8FB9]',
      bgGradient: 'from-[#D4A5B5]/10 to-[#5B8FB9]/10',
      borderColor: 'border-[#D4A5B5]/20',
      dotColor: 'bg-[#D4A5B5]',
    },
  ];

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="services"
      ref={ref}
      data-testid="services-section-v2"
      className="py-24 md:py-32 bg-gradient-to-br from-[#F8FCFA] via-white to-[#FDF8FA] relative overflow-hidden"
    >
      {/* Decorative elements */}
      <motion.div 
        animate={{ scale: [1, 1.1, 1] }}
        transition={{ duration: 8, repeat: Infinity }}
        className="absolute top-20 left-20 w-48 h-48 bg-gradient-to-br from-[#5B8FB9]/10 to-[#7CB98F]/10 rounded-full blur-2xl" 
      />
      <motion.div 
        animate={{ scale: [1.1, 1, 1.1] }}
        transition={{ duration: 10, repeat: Infinity }}
        className="absolute bottom-20 right-20 w-64 h-64 bg-gradient-to-br from-[#D4A5B5]/10 to-[#5B8FB9]/10 rounded-full blur-3xl" 
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 bg-gradient-to-r from-[#5B8FB9]/10 via-[#7CB98F]/10 to-[#D4A5B5]/10 text-[#5B8FB9] text-xs font-bold uppercase tracking-[0.2em] rounded-full mb-6 border border-[#7CB98F]/20">
            Services
          </span>
          <h2 
            className="text-4xl sm:text-5xl font-light tracking-tight text-gray-900"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
            data-testid="services-headline-v2"
          >
            How I Work
          </h2>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              whileHover={{ y: -8, scale: 1.02 }}
              className={`bg-gradient-to-br ${service.bgGradient} p-8 md:p-10 rounded-[2rem] border ${service.borderColor} hover:shadow-xl transition-all duration-300 flex flex-col h-full backdrop-blur-sm`}
              data-testid={`service-card-v2-${index + 1}`}
            >
              {/* Icon */}
              <div className={`w-16 h-16 rounded-full bg-gradient-to-r ${service.gradient} flex items-center justify-center mb-6 shadow-lg`}>
                <service.icon className="w-8 h-8 text-white" />
              </div>

              {/* Title */}
              <h3 
                className="text-2xl font-medium text-gray-900 mb-4"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {service.title}
              </h3>

              {/* Description */}
              <p className="text-gray-600 font-light leading-relaxed mb-6">
                {service.description}
              </p>

              {/* Outcomes */}
              <div className="mb-8 flex-grow">
                <p className="text-xs font-bold uppercase tracking-[0.15em] bg-gradient-to-r from-[#5B8FB9] to-[#7CB98F] bg-clip-text text-transparent mb-3">Outcomes:</p>
                <ul className="space-y-2">
                  {service.outcomes.map((outcome, i) => (
                    <li key={i} className="flex items-center gap-3 text-gray-600 text-sm">
                      <span className={`w-2 h-2 ${service.dotColor} rounded-full`} />
                      {outcome}
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className={`w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r ${service.gradient} text-white font-medium rounded-full transition-all duration-300 shadow-md hover:shadow-lg`}
                data-testid={`service-cta-v2-${index + 1}`}
              >
                {service.cta}
                <HiArrowRight className="w-4 h-4" />
              </motion.button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSectionV2;
